import { makeAutoObservable, runInAction } from "mobx";

import { showToast } from "@/lib/utils/toast";
import { surveysService } from "../services";

const normalizeStandardQuestion = (question, surveyId) => ({
  ...question,
  _id:
    question._id ||
    `${surveyId}-${question.order || 0}-${question.questionText}`,
  originType: "standard",
  parentQuestionId: question._id,
});

const applyQuestionFilters = (question, search, category) => {
  if (category !== "all" && question.category !== category) return false;
  if (search.trim()) {
    const term = search.trim().toLowerCase();
    return (
      question.questionText?.toLowerCase().includes(term) ||
      question.description?.toLowerCase().includes(term)
    );
  }
  return true;
};

class SurveyQuestionLibraryStore {
  standardSurveys = [];
  selectedSurveyId = "";
  surveyQuestions = [];
  loadingQuestions = false;
  initializing = true;
  search = "";
  categoryFilter = "all";
  selectedQuestions = [];
  selectedQuestion = null;
  showOverrides = false;

  constructor() {
    makeAutoObservable(this);
  }

  get filteredQuestions() {
    return this.surveyQuestions.filter((question) =>
      applyQuestionFilters(question, this.search, this.categoryFilter),
    );
  }

  get selectedQuestionIds() {
    return this.selectedQuestions.map((question) => question._id);
  }

  resetState = () => {
    this.standardSurveys = [];
    this.selectedSurveyId = "";
    this.surveyQuestions = [];
    this.loadingQuestions = false;
    this.initializing = true;
    this.search = "";
    this.categoryFilter = "all";
    this.selectedQuestions = [];
    this.selectedQuestion = null;
    this.showOverrides = false;
  };

  initialize = async () => {
    runInAction(() => {
      this.initializing = true;
    });
    await this.fetchStandardSurveys();
    if (this.selectedSurveyId) {
      await this.fetchSurveyQuestions(this.selectedSurveyId);
    }
    runInAction(() => {
      this.initializing = false;
    });
  };

  setSearch = (value) => {
    this.search = value;
  };

  setCategoryFilter = (value) => {
    this.categoryFilter = value;
  };

  setSelectedSurveyId = async (surveyId) => {
    this.selectedSurveyId = surveyId;
    this.selectedQuestions = [];
    this.selectedQuestion = null;
    this.showOverrides = false;
    await this.fetchSurveyQuestions(surveyId);
  };

  openOverrides = (question) => {
    this.selectedQuestion = question;
    this.showOverrides = true;
  };

  closeOverrides = () => {
    this.showOverrides = false;
    this.selectedQuestion = null;
  };

  toggleSelectedQuestion = (question) => {
    const questionId = question?._id;
    if (!questionId) return;
    const exists = this.selectedQuestions.some(
      (item) => item._id === questionId,
    );
    this.selectedQuestions = exists
      ? this.selectedQuestions.filter((item) => item._id !== questionId)
      : [...this.selectedQuestions, question];
  };

  clearSelectedQuestions = () => {
    this.selectedQuestions = [];
  };

  removeSelectedQuestion = (questionId) => {
    this.selectedQuestions = this.selectedQuestions.filter(
      (question) => question._id !== questionId,
    );
  };

  fetchStandardSurveys = async () => {
    try {
      const data = await surveysService.listStandard();
      const payload = data?.data || data;
      const published = (payload?.surveys || []).filter(
        (survey) => survey.status === "published",
      );
      runInAction(() => {
        this.standardSurveys = published;
        if (!this.selectedSurveyId && published.length) {
          this.selectedSurveyId = published[0]._id;
        }
      });
    } catch (err) {
      showToast.error(err?.message || "Unable to load standard surveys.");
      runInAction(() => {
        this.standardSurveys = [];
      });
    }
  };

  fetchSurveyQuestions = async (surveyId) => {
    if (!surveyId) {
      runInAction(() => {
        this.surveyQuestions = [];
      });
      return;
    }
    runInAction(() => {
      this.loadingQuestions = true;
    });
    try {
      const data = await surveysService.getStandardSurvey(surveyId);
      const payload = data?.data || data;
      const survey = payload?.survey || payload;
      const normalized = (survey?.questions || []).map((question) =>
        normalizeStandardQuestion(question, surveyId),
      );
      runInAction(() => {
        this.surveyQuestions = normalized;
      });
    } catch (err) {
      showToast.error(err?.message || "Unable to load survey questions.");
      runInAction(() => {
        this.surveyQuestions = [];
      });
    } finally {
      runInAction(() => {
        this.loadingQuestions = false;
      });
    }
  };
}

export const surveyQuestionLibraryStore = new SurveyQuestionLibraryStore();
